import React, { useEffect, useRef } from "react"
import { Navigate, Route, BrowserRouter as Router, Routes } from "react-router-dom"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { StyledAuthenticator } from "./components/StyledAuthenticator"
import { AppLayout } from "./components/layout"
import { Toaster } from "./components/ui/toaster"

// V2 Pages (Redesigned)
import LegacyDashboardPage from "./pages/v2/DashboardPage"
import ConfigurableDashboardPage from "./pages/v2/ConfigurableDashboardPage"
import CampaignsPage from "./pages/v2/CampaignsPage"
import CampaignDetailPage from "./pages/v2/CampaignDetailPage"
import CarePlansPage from "./pages/v2/CarePlansPage"
import AnalyticsPage from "./pages/v2/AnalyticsPage"
import ApcmOpsPage from "./pages/v2/ApcmOpsPage"
import TcmPage from "./pages/v2/TcmPage"
import { TcmCasePage, TcmResourcesPage } from "@/features/tcm"
import OutreachInboxPage from "./pages/v2/OutreachInboxPage"
import { useAuthStore } from "@/stores/authStore"
import {
  useCurrentOrgRole,
  canAccessApcmOps,
  canAccessDemoCalls,
} from "@/lib/useCurrentOrgRole"
import DemoCallsPage from "./pages/v2/DemoCallsPage"
import BillingCodesPage from "./pages/v2/BillingCodesPage"
import SettingsPage from "./pages/v2/SettingsPage"
import PatientDetailPage from "./pages/v2/PatientDetailPage"

// Existing Pages (To be redesigned)
import ActionItemsPage from "./pages/ActionItemsPage"
import ActionItemPage from "./pages/ActionItemPage"
import PatientsPage from "./pages/PatientsPage"
import PatientEditPage from "./pages/PatientEditPage"
import NewPatientPage from "./pages/NewPatientPage"
import CallsPage from "./pages/CallsPage"
import CallPage from "./pages/CallPage"

import ClinicDemoRoute from "./features/clinic-operations/DemoRoute"
import ClinicOperationsPage from "./features/clinic-operations/ClinicOperationsPage"

// Admin Pages
import MainSystemPromptPage from "./pages/Admin/MainSystemPromptPage"
import OrganizationDetails from "./pages/Admin/OrganizationDetails"
import SimulationLab from "./pages/Admin/SimulationLab"
import TestGenPage from "./pages/Admin/TestGenPage"
import VerifiedPhoneNumbersPage from "./pages/Admin/VerifiedPhoneNumbersPage"

// Legacy Pages (Keep for now)
import BotsPage from "./pages/BotsPage"
import BotDetailPage from "./pages/BotDetailPage"
import SchedulingPage from "./pages/SchedulingPage"
import Strategies from "./pages/Strategies"
import StrategyDetails from "./pages/StrategyDetails"
import OrganizationSettings from "./pages/OrganizationSettings"
import UpcomingCallsPage from "./pages/UpcomingCallsPage"

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      retry: 1,
    },
  },
})

const ApcmOpsRoute: React.FC = () => {
  const user = useAuthStore((s) => s.user)
  const role = useCurrentOrgRole()
  if (!canAccessApcmOps(role, !!user?.is_superuser)) {
    return <Navigate to="/dashboard" replace />
  }
  return <ApcmOpsPage />
}

// TCM shows discharges and clinical notes, so it has APCM ops' gate (no STAFF).
const TcmRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const user = useAuthStore((s) => s.user)
  const role = useCurrentOrgRole()
  if (!canAccessApcmOps(role, !!user?.is_superuser)) {
    return <Navigate to="/dashboard" replace />
  }
  return children
}

const DemoCallsRoute: React.FC = () => {
  const user = useAuthStore((s) => s.user)
  const role = useCurrentOrgRole()
  if (!canAccessDemoCalls(role, !!user?.is_superuser)) {
    return <Navigate to="/dashboard" replace />
  }
  return <DemoCallsPage />
}

/**
 * Guard for the /admin/* screens.
 *
 * These had no client-side guard at all: any signed-in user who typed the URL
 * got the organization admin, phone-number and system-prompt pages. The APIs
 * behind them are superuser-gated, so this was a confusing broken-looking page
 * rather than a data leak — but it also meant an impersonated session could
 * reach admin screens the target could never open, which defeats the point of
 * "view as user".
 */
const SuperuserRoute: React.FC<{ children: React.ReactElement }> = ({
  children,
}) => {
  const user = useAuthStore((s) => s.user)
  const isLoading = useAuthStore((s) => s.isLoading)
  // Wait for the user to load rather than bouncing on the first render.
  if (isLoading || user === null) return null
  if (!user.is_superuser) return <Navigate to="/dashboard" replace />
  return children
}

/**
 * Sign-out (or any change of signed-in user) must not leave the previous
 * identity's cached responses, such as organization members, in memory.
 * Impersonation clears the cache itself (useImpersonation).
 */
const IdentityCacheGuard: React.FC = () => {
  const userId = useAuthStore((s) => s.user?.id ?? null)
  const previous = useRef<string | null>(null)
  useEffect(() => {
    if (previous.current && previous.current !== userId) queryClient.clear()
    previous.current = userId
  }, [userId])
  return null
}

const AppContent: React.FC = () => {
  return (
    <StyledAuthenticator>
      <Routes>
        <Route element={<AppLayout />}>
          {/* Dashboard */}
          {/* Clinics land on their action items first */}
          <Route path="/" element={<Navigate to="/action-items" replace />} />
          <Route path="/dashboard" element={<ConfigurableDashboardPage />} />
          <Route path="/dashboard/:dashboardId" element={<ConfigurableDashboardPage />} />
          <Route path="/dashboard-legacy" element={<LegacyDashboardPage />} />

          {/* Patients */}
          <Route path="/patients" element={<PatientsPage />} />
          <Route path="/patients/:id" element={<PatientDetailPage />} />
          <Route path="/patients/:id/edit" element={<PatientEditPage />} />
          <Route path="/patients/new" element={<NewPatientPage />} />

          {/* Campaigns */}
          <Route path="/campaigns" element={<CampaignsPage />} />
          <Route path="/campaigns/:id" element={<CampaignDetailPage />} />

          {/* Outreach inbox (linked from the clinic's weekly report email) */}
          <Route path="/outreach/inbox" element={<OutreachInboxPage />} />

          {/* Calls */}
          <Route path="/calls" element={<CallsPage />} />
          <Route path="/calls/:id" element={<CallPage />} />
          <Route path="/upcoming-calls" element={<UpcomingCallsPage />} />
          <Route path="/demo-calls" element={<DemoCallsRoute />} />

          {/* Action Items */}
          <Route path="/action-items" element={<ActionItemsPage />} />
          {/* One item; the urgent action item text links here. Picks its own organization. */}
          <Route path="/action-items/:id" element={<ActionItemPage />} />

          {/* Care Plans */}
          <Route path="/care-plans" element={<CarePlansPage />} />

          {/* Analytics */}
          <Route path="/analytics" element={<AnalyticsPage />} />

          {/* APCM Operations */}
          <Route path="/apcm-ops" element={<ApcmOpsRoute />} />

          {/* Transitional care (TCM) */}
          <Route path="/tcm" element={<TcmRoute><TcmPage /></TcmRoute>} />
          <Route path="/tcm/case/:episodeId" element={<TcmRoute><TcmCasePage /></TcmRoute>} />
          <Route path="/tcm/resources" element={<TcmRoute><TcmResourcesPage /></TcmRoute>} />

          {/* Billing Codes */}
          <Route path="/billing-codes" element={<BillingCodesPage />} />

          {/* Settings */}
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/settings/*" element={<SettingsPage />} />
          <Route path="/organization/settings" element={<OrganizationSettings />} />

          {/* Legacy Routes (to be integrated/removed later) */}
          <Route path="/bots" element={<BotsPage />} />
          <Route path="/bots/:id" element={<BotDetailPage />} />
          <Route path="/scheduling" element={<SchedulingPage />} />
          <Route path="/strategies" element={<Strategies />} />
          <Route path="/strategy/:strategyId" element={<StrategyDetails />} />

          <Route path="/clinic-operations/demo" element={<ClinicDemoRoute />} />

          {/* Admin Routes (superuser only) */}
          <Route path="/admin/clinic-operations" element={<SuperuserRoute><ClinicOperationsPage /></SuperuserRoute>} />
          <Route
            path="/test-gen-basic"
            element={<SuperuserRoute><SimulationLab /></SuperuserRoute>}
          />
          <Route
            path="/test-gen"
            element={<SuperuserRoute><TestGenPage /></SuperuserRoute>}
          />
          <Route
            path="/admin/organization"
            element={<SuperuserRoute><OrganizationDetails /></SuperuserRoute>}
          />
          <Route
            path="/admin/numbers"
            element={<SuperuserRoute><VerifiedPhoneNumbersPage /></SuperuserRoute>}
          />
          <Route
            path="/admin/main-system-prompt"
            element={<SuperuserRoute><MainSystemPromptPage /></SuperuserRoute>}
          />

          {/* Catch-all redirect */}
          <Route path="*" element={<Navigate to="/action-items" replace />} />
        </Route>
      </Routes>
    </StyledAuthenticator>
  )
}

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <IdentityCacheGuard />
        <Routes>
          <Route path="/*" element={<AppContent />} />
        </Routes>
        <Toaster />
      </Router>
    </QueryClientProvider>
  )
}

export default App
